// components/VideoCard.jsx — Anamoria SPA
// v1.11 — NEW-163 Increment B (2026-10-30): (a) NEW-164 SUPERSEDED — the footer MediaTypeChip is
//   removed (the chip now lives in the response-card header, GroupMemoryCard); the spaceIsGroup
//   prop is kept (plumbing only, inert). Non-group spaces were already chip-free → byte-identical.
//   (b) additive `headerSlot` / `footerSlot` / `footerEndSlot` props (default null): header slot first
//   inside the card chrome; footer slot replaces the date attribution; footer-end slot sits just before the
//   privacy icon — so MemoryFeed v2.47 can place the response-card header, "Today" and the type chip inside a member's video card.
// v1.10 — NEW-164 (2026-10-25): group-feed media-type chip in the footer (Video), gated on
//   the new spaceIsGroup prop; presentational, no behaviour change.
// v1.9 — NEW-105 (2026-09-26): footer prefers the moment day (dayLabel = memory.dayText?.trim() || date).
// v1.8 — Feed video controls (embedded/feed card, Sept 1, 2026):
//   - Convention centered play affordance over the poster on a soft radial
//     scrim (pointer-only, aria-hidden); fades while the video is mounted,
//     returns on quiet reset. Mitigates the 2026-07-20 centered-disc removal
//     (soft scrim, not an opaque disc over the face).
//   - Live elapsed/total counter in the corner pill during playback
//     (onTimeUpdate). Pill no longer fades during playback (css v1.7) so the
//     pause control + counter stay on screen.
//   - The labeled/keyboard control remains .posterPlayPill; scrubber stays in
//     MediaLightbox. Embedded branch ONLY — legacy embedded=false unchanged.
// v1.7 — DF-7 feed chrome: favorite heart glyph 16 -> 20px (member overlay only).
// v1.6 — feed_redesign_v2: additive `redesign` prop → .cardV2 bordered chrome
//   when set (feed, non-embedded). Falsy elsewhere ⇒ byte-identical. (July 27, 2026)
// v1.5 — Embedded facade + on-poster play (3.1-Vb, July 20, 2026)
//
// Changes from v1.4 (momentcard-3-1Vb-playrow-latecover v0.2 §F, dispositioned;
// apply draft v0.3; all changes behind `embedded` — legacy path byte-identical):
//   - B2b facade: no <video> element in the feed until play is tapped —
//     idle renders poster <img> (or .posterPlaceholder while the async
//     pipeline has no poster yet); tap mounts <video> and plays; ended
//     unmounts back to the facade (F-Vb3, §1.10 quiet-reset ethos).
//   - On-poster control (.posterPlayPill): ONE combined play/pause +
//     duration pill, bottom-left (dark badge treatment). Gate trail — A2
//     centered 44px circle sat over the person's face [SOPHIE 2026-07-20]
//     → A3-hardened 32px corner circle → separate circle + badge competed
//     as two anchors on one line [SOPHIE] → combined pill (full trail in
//     css header). Duration badge renders alone only when unplayable
//     (pipeline processing — no dead control). During playback the pill
//     fades but stays in the DOM for keyboard access (F-Vb4); tap on the
//     video also toggles.
//   - playRow + "Video" label no longer render when embedded (F-Vb1,
//     G-3-consistent; NOT the §5.3 label retirement — legacy keeps it).
//   - Aria strings "Play video"/"Pause video" reused verbatim — zero new strings.
//
// v1.4 — Additive `embedded` prop for MomentCard presence slot (July 20, 2026)
//
// Changes from v1.3 (3.1; momentcard-3-1-DRAFT-for-review-v0.4.md §3, D-3.14/F-1):
//   - NEW prop `embedded = false`. When true (only MomentCard passes it):
//     favorite/edit overlay, own title, own footer, and root button semantics
//     (onClick/role/tabIndex) are conditionally NOT rendered. Poster, duration
//     badge, and playRow (play button + "Video" label) all kept — playback is
//     content.
//   - Legacy call sites pass nothing ⇒ default false ⇒ byte-identical render.
//   - CSS: one appended class .cardEmbedded (cursor default).
//
// v1.3 — Contributor name attribution in footer (June 27, 2026)
//
// Changes from v1.2:
//   - Footer date span now shows "Name · date" for contributor memories
//     (creatorType === 'contributor' && creatorName). Owner memories show
//     plain date as before. creatorName sourced from anamoria-memories v2.14
//     (LEFT JOIN contributors in feed query).
//   - Zero other changes. Poster, playback, overlay — all byte-identical
//     to v1.2.
//
// v1.2 — Photo card pattern, play + Video on same line (May 16, 2026)
//
// Follows photo card structure exactly:
//   - Borderless card, poster fills top, transparent content below
//   - Duration badge on poster (bottom-right)
//   - Play button + "Video" label on same line (no duplicate duration)
//   - Title below
//   - Date + privacy footer, transparent, no border
//   - Favorite/edit hover overlay inside poster area
//   - "Video" label in black (#1f2937) — same style as "Photo" label

import { useState, useRef, useCallback } from 'react';
import styles from './VideoCard.module.css';

/* ─── Helpers ─── */

function formatDuration(val) {
  const s = Math.round(Number(val) || 0);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

/* ─── SVG Icons ─── */

function HeartIcon({ filled }) {
  if (filled) {
    return (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  );
}

function PrivacyIcon({ isPrivate }) {
  if (isPrivate) {
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#737373" strokeWidth="1.5" strokeLinecap="round">
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#737373" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

/* ═══════════════════════════════════════
   VideoCard Component
   ═══════════════════════════════════════ */

export default function VideoCard({ memory, onFavorite, onEdit, onClick, embedded = false, redesign = false, spaceIsGroup = false, headerSlot = null, footerSlot = null, footerEndSlot = null }) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0); // v1.8: live counter (embedded)
  const videoRef = useRef(null);
  // v1.5 (embedded facade, B2b): no <video> until first play tap
  const [facadeMounted, setFacadeMounted] = useState(false);

  const posterUrl = memory.videoNote?.posterUrl || null;
  const playbackUrl = memory.videoNote?.playbackUrl || null;
  const duration = memory.videoNote?.durationSeconds || 0;
  const title = memory.title || '';
  const isPrivate = memory.isPrivate;
  const isFavorite = memory.isFavorite;
  const createdAt = memory.createdAt;
  const dayLabel = memory.dayText?.trim() || formatDate(createdAt);

  // v1.3: Contributor name attribution
  const attribution = (memory.creatorType === 'contributor' && memory.creatorName)
    ? `${memory.creatorName} \u00b7 ${dayLabel}`
    : dayLabel;

  /* ─── Playback ─── */

  const handlePlayPause = useCallback((e) => {
    e.stopPropagation();
    if (loading) return;

    const video = videoRef.current;
    if (!video) return;

    if (playing) {
      video.pause();
      setPlaying(false);
    } else {
      setLoading(true);
      video.play()
        .then(() => { setPlaying(true); setLoading(false); })
        .catch((err) => { console.error('Video play error:', err); setLoading(false); });
    }
  }, [playing, loading]);

  const handleVideoEnded = useCallback(() => { setPlaying(false); }, []);
  const handleVideoPause = useCallback(() => { setPlaying(false); }, []);

  // v1.8: live elapsed counter (embedded facade). Only the playing card
  // re-renders; ~4x/sec. NOT an aria-live region (avoids per-second SR spam).
  const handleTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (v) setCurrentTime(v.currentTime);
  }, []);

  /* v1.5 — embedded facade (B2b) */

  const handleFacadePlay = useCallback((e) => {
    e.stopPropagation();
    if (!facadeMounted) {
      setLoading(true);
      setFacadeMounted(true);
      return; // play fires from the ref callback once the video mounts
    }
    handlePlayPause(e);
  }, [facadeMounted, handlePlayPause]);

  const facadeVideoRef = useCallback((node) => {
    videoRef.current = node;
    if (node) {
      node.play()
        .then(() => { setPlaying(true); setLoading(false); })
        .catch((err) => { console.error('Video play error:', err); setLoading(false); });
    }
  }, []);

  const handleFacadeEnded = useCallback(() => {
    setPlaying(false);
    setFacadeMounted(false); // quiet reset to the poster facade (F-Vb3)
    setCurrentTime(0); // v1.8: reset counter on quiet reset
  }, []);

  /* ─── Card interactions ─── */

  const handleFavorite = useCallback((e) => {
    e.stopPropagation();
    if (onFavorite) onFavorite(memory);
  }, [memory, onFavorite]);

  const handleEdit = useCallback((e) => {
    e.stopPropagation();
    if (onEdit) onEdit(memory);
  }, [memory, onEdit]);

  const handleCardClick = useCallback(() => {
    if (onClick) onClick(memory);
  }, [memory, onClick]);

  return (
    <div
      className={`${styles.card}${embedded ? ` ${styles.cardEmbedded}` : ''}${redesign && !embedded ? ` ${styles.cardV2}` : ''}`}
      onClick={embedded ? undefined : handleCardClick}
      role={embedded ? undefined : 'button'}
      tabIndex={embedded ? undefined : 0}
    >
      {headerSlot}
      {/* Poster area — fills top, borderless.
          v1.5: embedded renders the B2b facade — no <video> until play is
          tapped. Legacy branch below is v1.4 verbatim (byte-identical). */}
      {embedded ? (
        <div className={styles.posterWrap}>
          {facadeMounted ? (
            <video
              ref={facadeVideoRef}
              className={styles.posterMedia}
              src={playbackUrl}
              poster={posterUrl || undefined}
              playsInline
              onClick={handlePlayPause}
              onEnded={handleFacadeEnded}
              onPause={handleVideoPause}
              onPlay={() => setPlaying(true)}
              onTimeUpdate={handleTimeUpdate}
            />
          ) : posterUrl ? (
            <img
              className={styles.posterMedia}
              src={posterUrl}
              alt={title || 'Video'}
              loading="lazy"
            />
          ) : (
            <div className={styles.posterPlaceholder}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
                <rect x="2" y="5" width="14" height="14" rx="2" />
                <path d="M16 9.5l6-3.5v12l-6-3.5" />
              </svg>
            </div>
          )}

          {/* v1.8: centered play — convention affordance (pointer-only,
              aria-hidden); the labeled/keyboard control is the pill below.
              Soft radial scrim (not an opaque disc) keeps it off the face;
              fades while mounted, returns on quiet reset. Rendered only when
              playable — no dead control while the pipeline is processing.
              stopPropagation (in handleFacadePlay) plays inline; taps
              elsewhere on the card navigate (MomentCard link). */}
          {playbackUrl && (
            <div
              className={`${styles.posterPlayCenter}${facadeMounted ? ` ${styles.posterPlayCenterHidden}` : ''}`}
              onClick={handleFacadePlay}
              aria-hidden="true"
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 4l14 8-14 8V4z" />
              </svg>
            </div>
          )}

          {/* Combined control: play/pause + duration/counter in ONE dark pill,
              bottom-right. During playback shows elapsed/total (v1.8) and stays
              visible (css v1.7). Unplayable falls back to the plain badge — no
              dead control. This is the labeled/keyboard control (F-Vb4). */}
          {playbackUrl ? (
            <button
              className={`${styles.posterPlayPill}${playing ? ` ${styles.posterPlayPillPlaying}` : ''}`}
              onClick={handleFacadePlay}
              aria-label={playing ? 'Pause video' : 'Play video'}
            >
              {loading ? (
                <div className={styles.playLoading} />
              ) : playing ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
              ) : null}
              {/* v1.8: idle shows duration ONLY — the centered play is the sole
                  idle play affordance (no redundant second triangle in the pill).
                  The pause glyph returns during playback; the pill stays the
                  labeled/keyboard control (aria-label unchanged). */}
              <span>{playing ? `${formatDuration(currentTime)} / ${formatDuration(duration)}` : formatDuration(duration)}</span>
            </button>
          ) : (
            <span className={styles.durationBadge}>{formatDuration(duration)}</span>
          )}
        </div>
      ) : (
      <div className={styles.posterWrap}>
        {playbackUrl ? (
          <video
            ref={videoRef}
            className={styles.posterMedia}
            src={playbackUrl}
            poster={posterUrl || undefined}
            playsInline
            preload="metadata"
            onEnded={handleVideoEnded}
            onPause={handleVideoPause}
          />
        ) : posterUrl ? (
          <img
            className={styles.posterMedia}
            src={posterUrl}
            alt={title || 'Video'}
            loading="lazy"
          />
        ) : (
          <div className={styles.posterPlaceholder}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
              <rect x="2" y="5" width="14" height="14" rx="2" />
              <path d="M16 9.5l6-3.5v12l-6-3.5" />
            </svg>
          </div>
        )}

        {/* Duration badge — bottom-right on poster */}
        <span className={styles.durationBadge}>{formatDuration(duration)}</span>

        {/* Favorite/edit hover overlay — inside poster (not when embedded, v1.4) */}
        {!embedded && (
          <div className={`${styles.overlay} ${isFavorite ? styles.overlayLiked : ''}`}>
            <button
              className={`${styles.overlayBtn} ${isFavorite ? styles.overlayBtnLiked : ''}`}
              onClick={handleFavorite}
              aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              aria-pressed={isFavorite}
            >
              <HeartIcon filled={isFavorite} />
            </button>
            <button
              className={`${styles.overlayBtn} ${styles.overlayEdit} ${isFavorite ? styles.overlayEditWhenLiked : ''}`}
              onClick={handleEdit}
              aria-label="Edit memory"
            >
              <EditIcon />
            </button>
          </div>
        )}
      </div>
      )}

      {/* Content — transparent background, matches photo card.
          v1.5: legacy only — playRow + "Video" label leave embedded cards
          (F-Vb1, G-3-consistent); title/footer were already !embedded. */}
      {!embedded && (
      <div className={styles.content}>
        {/* Play button + Video label on same line */}
        <div className={styles.playRow}>
          <button
            className={styles.playBtn}
            onClick={handlePlayPause}
            aria-label={playing ? 'Pause video' : 'Play video'}
          >
            {loading ? (
              <div className={styles.playLoading} />
            ) : playing ? (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 4l14 8-14 8V4z" />
              </svg>
            )}
          </button>
          <span className={styles.label}>Video</span>
        </div>

        {/* Title (not when embedded — the MomentCard carries the title, v1.4) */}
        {!embedded && title && <p className={styles.title}>{title}</p>}
      </div>
      )}

      {/* Footer — transparent, no border, matches photo card (not when embedded, v1.4) */}
      {!embedded && (
        <div className={styles.footer}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {footerSlot || <span className={styles.date}>{attribution}</span>}   {/* v1.11: a response card's footer carries the gentle day here, the chip at the end */}
          </span>
          <span className={styles.privacyIcon} style={footerEndSlot ? { display: 'inline-flex', alignItems: 'center', gap: 6 } : undefined}>
            {footerEndSlot}
            <PrivacyIcon isPrivate={isPrivate} />
          </span>
        </div>
      )}
    </div>
  );
}
